/**
 * `SubscriptionCard` — view + assign the account's Phase-A
 * subscription record (`AdminSubscription`).
 *
 * Source of truth on the wire is the structured `subscription` field
 * on `AdminAccountDetailResponse` (NULL when the account has no
 * account_billing row at all). The legacy `BillingCard` (free-form
 * plan label + amount + renews_at) still sits alongside it for
 * back-compat with the iteration-1 admin.
 *
 * INERT (Phase A): assigning a subscription here does NOT enforce
 * limits and does NOT charge the merchant. The InertBanner inside
 * the card makes that explicit; the assignment is purely catalog
 * bookkeeping until enforcement (Phase C) + Stripe sync (Phase D)
 * ship.
 *
 * 409 (archived plan) handling: `assignSubscription` returns an
 * `ApiError` with `kind='conflict'` when the chosen plan_id is
 * archived. We surface that as an inline "pick an active plan"
 * banner and DON'T close the assign form, so the staff member can
 * just change their selection and re-submit.
 */

import { useEffect, useMemo, useState } from 'react';

import type {
  AdminPlanDetail,
  AdminPlanInterval,
  AdminSubscription,
  AdminSubscriptionAssignBody,
  AdminSubscriptionStatus,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { Field } from '../../components/atoms/Field.js';
import { Pill } from '../../components/atoms/Pill.js';
import { useC } from '../../lib/theme.js';
import {
  formatDateForInput,
  formatIsoCompact,
  formatMinorCurrency,
} from '../../lib/format.js';
import { InertBanner } from '../plans/InertBanner.js';
import {
  PLAN_INTERVALS,
  SUBSCRIPTION_STATUS_OPTIONS,
  findPrice,
  subscriptionStatusTone,
} from '../plans/plan-helpers.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";
const serifFont = "'Instrument Serif', serif";

interface AssignForm {
  plan_id: string;
  interval: AdminPlanInterval;
  status: AdminSubscriptionStatus;
  trial_ends_at: string;
  current_period_start: string;
  current_period_end: string;
  renews_at: string;
  amount_minor: string;
  currency: string;
  notes: string;
}

function blankAssignForm(): AssignForm {
  return {
    plan_id: '',
    interval: 'monthly',
    status: 'active',
    trial_ends_at: '',
    current_period_start: '',
    current_period_end: '',
    renews_at: '',
    amount_minor: '',
    currency: 'usd',
    notes: '',
  };
}

function subscriptionToForm(sub: AdminSubscription): AssignForm {
  return {
    plan_id: sub.plan_id ?? '',
    interval: sub.interval ?? 'monthly',
    status: sub.status,
    trial_ends_at: formatDateForInput(sub.trial_ends_at?.slice(0, 10) ?? null),
    current_period_start: formatDateForInput(
      sub.current_period_start?.slice(0, 10) ?? null,
    ),
    current_period_end: formatDateForInput(
      sub.current_period_end?.slice(0, 10) ?? null,
    ),
    renews_at: formatDateForInput(sub.renews_at),
    amount_minor: sub.amount_minor === null ? '' : String(sub.amount_minor),
    currency: sub.currency,
    notes: sub.notes ?? '',
  };
}

export interface SubscriptionCardProps {
  accountId: string;
  subscription: AdminSubscription | null;
  /** The account's Stripe customer ref, for the "View invoices" deep-link.
   *  Null until the merchant has started checkout at least once. */
  billingCustomerRef: string | null;
  onAssigned: (next: AdminSubscription) => void;
  /**
   * Called after a successful enforcement-toggle flip with the
   * post-flip boolean. The parent patches its local
   * `subscription.enforcement_enabled` (or re-fetches the account
   * when `subscription` was `null` before the flip — the server
   * upserts the `account_billing` shell row on first toggle).
   */
  onEnforcementChanged: (enabled: boolean) => void;
}

export function SubscriptionCard({
  accountId,
  subscription,
  billingCustomerRef,
  onAssigned,
  onEnforcementChanged,
}: SubscriptionCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [plans, setPlans] = useState<readonly AdminPlanDetail[]>([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState<string | null>(null);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<AssignForm>(() =>
    subscription === null ? blankAssignForm() : subscriptionToForm(subscription),
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [archivedError, setArchivedError] = useState(false);
  const [successAt, setSuccessAt] = useState<number | null>(null);

  // Re-seed the form when the subscription prop changes (e.g. after a
  // successful assign the parent passes a fresh value).
  useEffect(() => {
    setForm(
      subscription === null ? blankAssignForm() : subscriptionToForm(subscription),
    );
  }, [subscription]);

  // Lazy-load the plan catalog the first time the staff opens the
  // assign form — keeps the AccountDetail mount cost flat for the
  // common case (staff just views the account, no subscription edit).
  useEffect(() => {
    if (!open || plans.length > 0) return;
    let cancelled = false;
    setPlansLoading(true);
    setPlansError(null);
    (async (): Promise<void> => {
      try {
        const res = await api.getPlans();
        if (!cancelled) setPlans(res.plans);
      } catch (err) {
        if (cancelled) return;
        const reason =
          err instanceof ApiError && err.server_reason !== undefined
            ? ` (${err.server_reason})`
            : '';
        setPlansError(`Could not load plans${reason}.`);
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, open, plans.length]);

  const activePlans = useMemo(
    () => plans.filter((p) => p.plan.status === 'active'),
    [plans],
  );

  const selectedPlan = useMemo(
    () => plans.find((p) => p.plan.plan_id === form.plan_id) ?? null,
    [plans, form.plan_id],
  );

  // Display the plan's own price for the current interval as the
  // suggested amount; the override input shows the explicit value
  // only when staff entered one.
  const planSuggestedAmount = useMemo(() => {
    if (selectedPlan === null) return null;
    const price = findPrice(selectedPlan.prices, form.interval);
    return price ?? null;
  }, [selectedPlan, form.interval]);

  const update = <K extends keyof AssignForm>(key: K, v: AssignForm[K]): void => {
    setForm((cur) => ({ ...cur, [key]: v }));
    if (archivedError) setArchivedError(false);
  };

  const buildBody = (): AdminSubscriptionAssignBody => {
    const trimmedAmount = form.amount_minor.trim();
    const body: {
      plan_id: string;
      interval: AdminPlanInterval;
      status: AdminSubscriptionStatus;
      trial_ends_at?: string | null;
      current_period_start?: string | null;
      current_period_end?: string | null;
      renews_at?: string | null;
      amount_minor?: number | null;
      currency?: string;
      notes?: string | null;
    } = {
      plan_id: form.plan_id,
      interval: form.interval,
      status: form.status,
    };
    body.trial_ends_at =
      form.trial_ends_at.trim().length === 0 ? null : form.trial_ends_at.trim();
    body.current_period_start =
      form.current_period_start.trim().length === 0
        ? null
        : form.current_period_start.trim();
    body.current_period_end =
      form.current_period_end.trim().length === 0
        ? null
        : form.current_period_end.trim();
    body.renews_at =
      form.renews_at.trim().length === 0 ? null : form.renews_at.trim();
    body.amount_minor = trimmedAmount.length === 0 ? null : Number(trimmedAmount);
    body.currency = form.currency.trim().toLowerCase();
    body.notes = form.notes.trim().length === 0 ? null : form.notes.trim();
    return body;
  };

  const doAssign = async (): Promise<void> => {
    if (saving) return;
    if (form.plan_id.length === 0) {
      setSaveError('Pick a plan to assign.');
      return;
    }
    const trimmedAmount = form.amount_minor.trim();
    if (trimmedAmount.length > 0) {
      const n = Number(trimmedAmount);
      if (!Number.isFinite(n) || n < 0 || !Number.isInteger(n)) {
        setSaveError(
          'Amount override must be a non-negative whole number in minor units.',
        );
        return;
      }
    }
    setSaving(true);
    setSaveError(null);
    setArchivedError(false);
    try {
      const res = await api.assignSubscription(accountId, buildBody());
      onAssigned(res.subscription);
      setSuccessAt(Date.now());
      setOpen(false);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'conflict') {
        setArchivedError(true);
        setSaveError(
          "That plan is archived — pick an active one to assign.",
        );
        return;
      }
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setSaveError(`Could not assign subscription${reason}.`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ padding: 20 }}>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.muted,
            letterSpacing: 2,
            textTransform: 'uppercase',
          }}
        >
          Subscription
        </div>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.dim,
            letterSpacing: 1,
          }}
        >
          Billing · test mode
        </div>
      </div>

      <InertBanner variant="compact" />

      {subscription !== null ? (
        <SubscriptionSummary subscription={subscription} />
      ) : (
        <div
          className="mb-4 p-3"
          style={{
            background: C.surface2,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 12,
            color: C.muted,
          }}
        >
          No subscription assigned. Use the control below to assign one.
        </div>
      )}

      {billingCustomerRef !== null && billingCustomerRef.length > 0 ? (
        <a
          // Test-mode deep-link (billing is test-mode until the live cut-over);
          // update the `/test/` segment when BILLING_LIVE_GATE_PASSED flips.
          href={`https://dashboard.stripe.com/test/customers/${encodeURIComponent(billingCustomerRef)}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 mb-4"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.limeFg, textDecoration: 'none' }}
        >
          View invoices &amp; payment history in Stripe ↗
        </a>
      ) : null}

      <EnforcementToggleRow
        accountId={accountId}
        enforcementEnabled={subscription?.enforcement_enabled ?? false}
        hasPlan={subscription !== null && subscription.plan_id !== null}
        onChanged={onEnforcementChanged}
      />

      {successAt !== null && Date.now() - successAt < 4000 ? (
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            color: C.limeFg,
            letterSpacing: 2,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Subscription saved
        </div>
      ) : null}

      {!open ? (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setSaveError(null);
          }}
          style={{
            background: C.surface2,
            color: C.text,
            fontFamily: sansFont,
            fontSize: 13,
            fontWeight: 500,
            padding: '10px 14px',
            borderRadius: 2,
            border: `1px solid ${C.border}`,
            minHeight: 44,
          }}
        >
          {subscription === null ? 'Assign plan' : 'Change subscription'}
        </button>
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void doAssign();
          }}
          className="grid grid-cols-1 gap-4 mt-2"
        >
          <Field label="Plan" required>
            {plansLoading ? (
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 11,
                  color: C.muted,
                  letterSpacing: 1,
                  padding: '8px 0',
                }}
              >
                Loading plans…
              </div>
            ) : plansError !== null ? (
              <div
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.coralFg,
                }}
              >
                {plansError}
              </div>
            ) : (
              <select
                value={form.plan_id}
                onChange={(e) => update('plan_id', e.target.value)}
                className="w-full px-3 py-2 outline-none"
                style={inputStyle(C)}
                required
              >
                <option value="">— select a plan —</option>
                {activePlans.map((p) => (
                  <option key={p.plan.plan_id} value={p.plan.plan_id}>
                    {p.plan.name} ({p.plan.slug})
                  </option>
                ))}
                {/* If the currently-assigned plan is archived, surface
                    it disabled so the staff can SEE what's already set
                    without being able to re-pick it (the 409 also
                    catches the case). */}
                {plans
                  .filter(
                    (p) =>
                      p.plan.status !== 'active' && p.plan.plan_id === form.plan_id,
                  )
                  .map((p) => (
                    <option key={p.plan.plan_id} value={p.plan.plan_id} disabled>
                      {p.plan.name} ({p.plan.status})
                    </option>
                  ))}
              </select>
            )}
          </Field>

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Interval">
              <select
                value={form.interval}
                onChange={(e) =>
                  update('interval', e.target.value as AdminPlanInterval)
                }
                className="w-full px-3 py-2 outline-none"
                style={inputStyle(C)}
              >
                {PLAN_INTERVALS.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                value={form.status}
                onChange={(e) =>
                  update('status', e.target.value as AdminSubscriptionStatus)
                }
                className="w-full px-3 py-2 outline-none"
                style={inputStyle(C)}
              >
                {SUBSCRIPTION_STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Trial ends at" hint="Optional. Date (YYYY-MM-DD).">
              <input
                type="date"
                value={form.trial_ends_at}
                onChange={(e) => update('trial_ends_at', e.target.value)}
                className="w-full px-3 py-2 outline-none"
                style={{ ...inputStyle(C), fontFamily: monoFont }}
              />
            </Field>
            <Field label="Period start" hint="Optional.">
              <input
                type="date"
                value={form.current_period_start}
                onChange={(e) => update('current_period_start', e.target.value)}
                className="w-full px-3 py-2 outline-none"
                style={{ ...inputStyle(C), fontFamily: monoFont }}
              />
            </Field>
            <Field label="Period end" hint="Optional.">
              <input
                type="date"
                value={form.current_period_end}
                onChange={(e) => update('current_period_end', e.target.value)}
                className="w-full px-3 py-2 outline-none"
                style={{ ...inputStyle(C), fontFamily: monoFont }}
              />
            </Field>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <Field label="Renews at" hint="Calendar hint (YYYY-MM-DD).">
              <input
                type="date"
                value={form.renews_at}
                onChange={(e) => update('renews_at', e.target.value)}
                className="w-full px-3 py-2 outline-none"
                style={{ ...inputStyle(C), fontFamily: monoFont }}
              />
            </Field>
            <Field
              label="Amount override"
              hint={
                planSuggestedAmount !== null
                  ? `Plan price: ${formatMinorCurrency(
                      planSuggestedAmount.amount_minor,
                      planSuggestedAmount.currency,
                    )}. Blank = use plan price.`
                  : 'Blank = use plan price (minor units).'
              }
            >
              <input
                type="text"
                inputMode="numeric"
                value={form.amount_minor}
                onChange={(e) => update('amount_minor', e.target.value)}
                className="w-full px-3 py-2 outline-none"
                style={{ ...inputStyle(C), fontFamily: monoFont }}
                placeholder="—"
              />
            </Field>
            <Field label="Currency">
              <input
                type="text"
                value={form.currency}
                onChange={(e) => update('currency', e.target.value.toLowerCase())}
                maxLength={3}
                className="w-full px-3 py-2 outline-none uppercase"
                style={{
                  ...inputStyle(C),
                  fontFamily: monoFont,
                  textAlign: 'center',
                }}
                placeholder="usd"
              />
            </Field>
          </div>

          <Field
            label="Notes"
            hint="Staff-only. Never displayed to the merchant."
          >
            <textarea
              value={form.notes}
              onChange={(e) => update('notes', e.target.value)}
              rows={2}
              className="w-full px-3 py-2 outline-none"
              style={{ ...inputStyle(C), resize: 'vertical' }}
              placeholder="Internal notes — e.g. negotiated terms, contract ref."
            />
          </Field>

          {archivedError ? (
            <div
              className="p-3"
              style={{
                background: C.coralDim,
                border: `1px solid ${C.coralFg}`,
                borderRadius: 2,
                fontFamily: sansFont,
                fontSize: 12,
                color: C.text,
                lineHeight: 1.5,
              }}
            >
              That plan is archived. Pick one of the active plans from the
              dropdown to assign.
            </div>
          ) : saveError !== null ? (
            <div
              style={{
                fontFamily: sansFont,
                fontSize: 12,
                color: C.coralFg,
              }}
            >
              {saveError}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => {
                if (saving) return;
                setOpen(false);
                setSaveError(null);
                setArchivedError(false);
                // Re-seed to last known good.
                setForm(
                  subscription === null
                    ? blankAssignForm()
                    : subscriptionToForm(subscription),
                );
              }}
              disabled={saving}
              style={{
                background: 'transparent',
                color: C.muted,
                fontFamily: sansFont,
                fontSize: 13,
                padding: '10px 14px',
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                minHeight: 44,
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || form.plan_id.length === 0}
              style={{
                background: form.plan_id.length === 0 ? C.surface2 : C.lime,
                color: form.plan_id.length === 0 ? C.muted : C.ink,
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 600,
                padding: '10px 16px',
                borderRadius: 2,
                border: 0,
                opacity: saving ? 0.6 : 1,
                minHeight: 44,
              }}
            >
              {saving ? 'Saving…' : 'Save subscription'}
            </button>
          </div>
        </form>
      )}
    </Card>
  );
}

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  trialing: 'Free trial',
  past_due: 'Payment failed',
  suspended: 'Paused',
  closed: 'Ended',
  canceled: 'Canceled',
};

/** Format an ISO string as `YYYY-MM-DD HH:MM UTC` (staff surface = always UTC). */
function fmtUtc(iso: string | null): string {
  if (iso === null) return '—';
  try {
    const d = new Date(iso);
    const p = (n: number): string => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth() + 1)}-${p(d.getUTCDate())} ${p(d.getUTCHours())}:${p(d.getUTCMinutes())} UTC`;
  } catch {
    return iso;
  }
}

/** One human renewal/lifecycle line driven by status + current_period_end. */
function humanRenewal(sub: AdminSubscription): string {
  const end = sub.current_period_end;
  switch (sub.status) {
    case 'active':
      return end !== null ? `Renews ${fmtUtc(end)}` : 'Active — no renewal date';
    case 'trialing':
      return sub.trial_ends_at !== null ? `Trial ends ${fmtUtc(sub.trial_ends_at)}` : 'On trial';
    case 'past_due':
      return end !== null ? `Renewal failed — access until ${fmtUtc(end)}` : 'Payment failed';
    case 'canceled':
    case 'closed':
    case 'suspended': {
      const endMs = end !== null ? Date.parse(end) : null;
      if (endMs !== null && endMs > Date.now()) return `Access until ${fmtUtc(end)} · then reverts to Discovery`;
      return `Ended ${fmtUtc(sub.canceled_at ?? end)}`;
    }
    default:
      return '';
  }
}

function SubscriptionSummary({
  subscription,
}: {
  subscription: AdminSubscription;
}): JSX.Element {
  const C = useC();
  // The plan NAME is resolved server-side (LEFT JOIN plan) so staff see
  // "Scale" rather than a raw `pln_…` ULID — regardless of whether the
  // catalog list has finished loading in the card.
  const planName = subscription.plan_name;
  return (
    <div
      className="mb-4 p-3 grid gap-2"
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
      }}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            style={{
              fontFamily: serifFont,
              fontSize: 18,
              color: C.text,
              letterSpacing: -0.3,
            }}
          >
            {planName ?? (subscription.plan_id === null ? 'No plan' : 'Unknown plan')}
          </span>
          <Pill tone={subscriptionStatusTone(subscription.status)}>
            {STATUS_LABEL[subscription.status] ?? subscription.status}
          </Pill>
          {subscription.interval !== null ? (
            <span
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                color: C.body,
                letterSpacing: 0.5,
              }}
            >
              {subscription.interval}
            </span>
          ) : null}
        </div>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.body,
            fontWeight: 500,
          }}
        >
          {formatMinorCurrency(subscription.amount_minor, subscription.currency)}
        </span>
      </div>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 11,
          color: C.body,
          lineHeight: 1.6,
        }}
      >
        <div style={{ color: C.text, fontWeight: 500 }}>{humanRenewal(subscription)}</div>
        <div style={{ color: C.muted, marginTop: 4 }}>plan_id: {subscription.plan_id ?? '—'}</div>
        <div style={{ color: C.muted }}>
          period: {fmtUtc(subscription.current_period_start)} → {fmtUtc(subscription.current_period_end)}
        </div>
        {subscription.canceled_at !== null ? (
          <div style={{ color: C.muted }}>canceled: {fmtUtc(subscription.canceled_at)}</div>
        ) : null}
        <div style={{ color: C.muted }}>updated: {fmtUtc(subscription.updated_at)}</div>
      </div>
      {subscription.notes !== null && subscription.notes.length > 0 ? (
        <div
          className="mt-1 p-2"
          style={{
            background: C.surface,
            border: `1px solid ${C.borderSoft}`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 12,
            color: C.body,
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 9,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 4,
            }}
          >
            Notes (staff only)
          </div>
          {subscription.notes}
        </div>
      ) : null}
    </div>
  );
}

function inputStyle(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    fontFamily: sansFont,
    fontSize: 13,
    color: C.text,
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 2,
  };
}

/**
 * `EnforcementToggleRow` — per-account master switch for plan-limit
 * enforcement (ADR-0143 §8 / Phase C.1).
 *
 * Distinct amber/cautious styling so it reads as an operational
 * switch, NOT a cosmetic field — staff should pause before flipping.
 * Phase C.1 ships INERT (nothing reads it to block requests yet); the
 * helper copy is honest about that and about the soft-vs-hard
 * dimension split.
 *
 * Optimistic UI: we flip the visual state immediately, then call the
 * API. On failure we revert and surface the server reason. The
 * `pending` flag disables the switch + dims the row while the PUT is
 * in-flight (one outstanding flip at a time).
 */
function EnforcementToggleRow({
  accountId,
  enforcementEnabled,
  hasPlan,
  onChanged,
}: {
  accountId: string;
  enforcementEnabled: boolean;
  hasPlan: boolean;
  onChanged: (enabled: boolean) => void;
}): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const doToggle = async (next: boolean): Promise<void> => {
    if (pending) return;
    setPending(true);
    setError(null);
    try {
      const res = await api.setEnforcement(accountId, next);
      onChanged(res.enforcement_enabled);
      setSavedAt(Date.now());
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setError(`Could not update enforcement${reason}.`);
    } finally {
      setPending(false);
    }
  };

  const switchId = `enforcement-toggle-${accountId}`;
  const hintId = `${switchId}-hint`;

  return (
    <div
      className="mb-4 p-3 sm:p-4"
      style={{
        background: C.amberDim,
        border: `1px solid ${C.amber}`,
        borderRadius: 2,
        opacity: pending ? 0.85 : 1,
        transition: 'opacity 120ms ease',
      }}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.amberFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Enforcement
          </div>
          <label
            htmlFor={switchId}
            style={{
              fontFamily: sansFont,
              fontSize: 14,
              color: C.text,
              fontWeight: 500,
              display: 'block',
              cursor: pending ? 'wait' : 'pointer',
            }}
          >
            Enforce plan limits for this account
          </label>
        </div>
        <EnforcementSwitch
          id={switchId}
          checked={enforcementEnabled}
          pending={pending}
          ariaDescribedBy={hintId}
          onChange={(next): void => {
            void doToggle(next);
          }}
        />
      </div>

      <div
        id={hintId}
        className="mt-2"
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          color: C.body,
          lineHeight: 1.5,
        }}
      >
        Per-account master switch. OFF (default) = usage is tracked but
        nothing is blocked. Turning it ON enforces the plan&rsquo;s HARD caps
        (sites + red-team) — requests over the limit are rejected. Soft
        dimensions (transactions, traffic) are never blocked. A churned
        account enforces the free-tier caps.
      </div>

      {!hasPlan ? (
        <div
          className="mt-2"
          style={{
            fontFamily: monoFont,
            fontSize: 11,
            color: C.muted,
            letterSpacing: 0.3,
            lineHeight: 1.5,
          }}
        >
          No plan assigned — enforcement with no plan is unlimited
          (nothing to cap). The toggle is harmless until a plan is
          assigned.
        </div>
      ) : null}

      <div
        className="mt-2 flex items-center gap-3 flex-wrap"
        aria-live="polite"
        style={{ minHeight: 16 }}
      >
        {pending ? (
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.muted,
              letterSpacing: 2,
              textTransform: 'uppercase',
            }}
          >
            Saving…
          </span>
        ) : error !== null ? (
          <span
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.coralFg,
            }}
          >
            {error}
          </span>
        ) : savedAt !== null && Date.now() - savedAt < 4000 ? (
          <span
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.amberFg,
              letterSpacing: 2,
              textTransform: 'uppercase',
            }}
          >
            Saved
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * `EnforcementSwitch` — visual switch built on a native checkbox for
 * accessibility (keyboard, screen readers, form semantics) with a
 * custom amber-themed track + thumb.
 *
 * The native `<input type="checkbox">` is visually hidden but
 * remains in the tab order and receives focus; the styled track sits
 * underneath via absolute positioning. Tap target ≥ 44×44 so the row
 * meets the mobile-touch minimum.
 */
function EnforcementSwitch({
  id,
  checked,
  pending,
  ariaDescribedBy,
  onChange,
}: {
  id: string;
  checked: boolean;
  pending: boolean;
  ariaDescribedBy: string;
  onChange: (next: boolean) => void;
}): JSX.Element {
  const C = useC();
  const TRACK_W = 44;
  const TRACK_H = 24;
  const THUMB = 18;
  const PAD = 3;
  return (
    <label
      htmlFor={id}
      className="relative inline-flex items-center justify-center flex-shrink-0"
      style={{
        width: 44,
        height: 44,
        cursor: pending ? 'wait' : 'pointer',
      }}
    >
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={pending}
        aria-checked={checked}
        aria-describedby={ariaDescribedBy}
        onChange={(e): void => onChange(e.target.checked)}
        // Visually hidden but focusable + in the form/tree.
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0,0,0,0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
      <span
        aria-hidden="true"
        style={{
          position: 'relative',
          display: 'inline-block',
          width: TRACK_W,
          height: TRACK_H,
          background: checked ? C.amber : C.surface2,
          border: `1px solid ${checked ? C.amber : C.border}`,
          borderRadius: 999,
          transition: 'background 140ms ease, border-color 140ms ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: PAD - 1,
            left: checked ? TRACK_W - THUMB - PAD - 1 : PAD - 1,
            width: THUMB,
            height: THUMB,
            background: checked ? C.ink : C.text,
            borderRadius: '50%',
            transition: 'left 140ms ease, background 140ms ease',
            boxShadow: checked
              ? '0 1px 2px rgba(0,0,0,0.25)'
              : '0 1px 2px rgba(0,0,0,0.15)',
          }}
        />
      </span>
    </label>
  );
}
